import React, { useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider, useMediaQuery } from "@mui/material";
import "./styles/App.css";
import NotificationSelector from "./NotificationSelector";
import { useInitStore } from "./Hooks/Store";
import { logoutUser } from "./services/loginService";
import generateTheme, { ThemeContext } from "./theme";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { usePageLoadTimer } from "./Hooks/PageLoadHook/usePageLoadTimer";
import ApplicationRoutes from "./rewamp/routes/RewampRoutes";
import { AppLayoutContext } from "./contexts/AppLayoutContext";
import { NavigationGuardContext } from "./contexts/NavigationGuardContext";
import { BreadcrumbStackProvider } from "./rewamp/contexts/BreadcrumbStackContext";
import { REWAMP_NAV_HEIGHT } from "./rewamp/layouts/navMetrics";
import { AccessibilityProvider } from "./rewamp/context/AccessibilityContext";

window.contextPath = "";

function importAll(r) {
  r.keys().forEach(r);
}


function App() {
  usePageLoadTimer();
  const [fontSize, setFontSize] = useState(
    JSON.parse(sessionStorage.getItem("DFSAPP-FontSize")) ?? 16
  );
  const [ourTheme, setOurTheme] = useState(
    sessionStorage.getItem("DFSAPP-ourTheme") ?? "light"
  );
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:640px)");
  importAll(require.context('./', true, /\.css$/));

  const increaseFontSize = () => {
    setFontSize((prevSize) => Math.min(prevSize + 0.5, 18)); // Limit max font size to 32px
  };
  const resetTextSize = () => {
    setFontSize(16);
  };
  useEffect(() => {
    sessionStorage.setItem("DFSAPP-FontSize", JSON.stringify(fontSize));
    document.documentElement.style.fontSize = `${fontSize}px`;
  }, [fontSize]);

  // Mirror the nav-height single source (navMetrics.js) into a CSS var so
  // html { scroll-padding-top } stays in sync — same documentElement pattern
  // as the fontSize effect above.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--rewamp-nav-height",
      `${REWAMP_NAV_HEIGHT}px`
    );
  }, []);

  const toggleTheme = () => {
    setOurTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };
  useEffect(() => {
    sessionStorage.setItem("DFSAPP-ourTheme", ourTheme);
  }, [ourTheme]);

  const decreaseFontSize = () => {
    setFontSize((prevSize) => Math.max(prevSize - 0.5, 14));
  };

  const { data: initData } = useInitStore();
  const dispatch = useDispatch();
  const navigationGuardRef = useRef(null);

  const handleLogout = async () => {
    await logoutUser(dispatch);
    navigate(`${window.contextPath}/`);
  };

  const theme = useMemo(
    () => generateTheme(fontSize, ourTheme),
    [fontSize, ourTheme]
  );
  useEffect(() => {
    document.body.classList.toggle("light-theme", ourTheme === "light");
    document.body.classList.toggle("dark-theme", ourTheme === "dark");
  }, [ourTheme]);

  const themeContextValue = useMemo(() => ({ ourTheme, toggleTheme, isMobile }), [ourTheme, toggleTheme, isMobile]);

  const layoutContextValue = useMemo(() => ({
    handleLogout,
    decreaseFontSize,
    increaseFontSize,
    resetTextSize,
    ourTheme,
    initData,
    isMobile,
    fontSize,
  }), [handleLogout, decreaseFontSize, increaseFontSize, resetTextSize, ourTheme, initData, isMobile, fontSize]);

  return (
    <div className="app-container">
      <ThemeContext.Provider value={themeContextValue}>
        <ThemeProvider theme={theme}>
          <NotificationSelector>
            <AppLayoutContext.Provider value={layoutContextValue}>
              <NavigationGuardContext.Provider value={navigationGuardRef}>
                <BreadcrumbStackProvider>
                  <AccessibilityProvider>
                    <ApplicationRoutes isMobile={isMobile} initData={initData} />
                  </AccessibilityProvider>
                </BreadcrumbStackProvider>
              </NavigationGuardContext.Provider>
            </AppLayoutContext.Provider>
          </NotificationSelector>
        </ThemeProvider>
      </ThemeContext.Provider>
    </div>
  );
}

export default App;
